import { computed } from '@angular/core';
import {
  signalStoreFeature,
  type,
  withComputed,
  withMethods,
} from '@ngrx/signals';
import { GeoDataState } from './geo-data.state';
import {
  Feature,
  FeatureCollection,
  FeatureProperties,
  KeyValueDistricts,
} from '../../models/geo-data';
import { filterGeoJsonFeaturesByGroup } from './geo-utils';
import { StateFilter } from '../crud.state';

const filterItems = (items: FeatureProperties[], filter: StateFilter) => {
  return items.filter(item => {
    if (!item.name) return false;
    const titleMatch = item.name
      .toLowerCase()
      .includes(filter.query.toLowerCase());
    const keyMatch =
      !filter.ids || filter.ids.length === 0 || filter.ids.includes(item.key);

    return titleMatch && keyMatch;
  });
};

export function withGeoDataSelectors() {
  return signalStoreFeature(
    {
      state: type<GeoDataState>(),
    },
    withComputed(({ items, selectedId }) => ({
      count: computed(() => Object.keys(items()).length),
      asArray: computed(() => Object.values(items())),
      keysFromSelection: computed(() => {
        if (selectedId()) {
          const geoData = items()[selectedId()];
          if (!geoData || !geoData.geoJson) return [];
          const keys: string[] = [];
          const geoJson = JSON.parse(geoData.geoJson);
          const features = geoJson.features;
          features.forEach((feature: Feature) => {
            keys.push(feature.properties['key']);
          });
          return keys;
        } else {
          return [];
        }
      }),
      keyValueDistrictsFromSelection: computed(() => {
        if (selectedId()) {
          const geoData = items()[selectedId()];
          if (!geoData || !geoData.geoJson) return [];
          const geoJson = JSON.parse(geoData.geoJson);
          return geoJson.features.reduce(
            (acc: KeyValueDistricts, feature: Feature) => {
              const key = feature.properties?.key; // Assuming `key` is in `properties`
              if (key) {
                acc[key] = {
                  key: key,
                  name: feature.properties?.name || '',
                };
              }
              return acc;
            },
            {} as KeyValueDistricts
          );
        } else {
          return [];
        }
      }),
      featuresFromSelection: computed(() => {
        if (selectedId()) {
          const geoData = items()[selectedId()];
          if (!geoData || !geoData.geoJson) return [];
          const geoJson = JSON.parse(geoData.geoJson);
          return geoJson.features;
        } else {
          return [];
        }
      }),
      geoJsonFromSelection: computed(
        (): GeoJSON.FeatureCollection<
          GeoJSON.Point | GeoJSON.Polygon,
          GeoJSON.GeoJsonProperties
        > | null => {
          if (selectedId()) {
            const geoData = items()[selectedId()];
            if (!geoData || !geoData.geoJson) return null;
            return JSON.parse(geoData.geoJson) as GeoJSON.FeatureCollection<
              GeoJSON.Point | GeoJSON.Polygon,
              GeoJSON.GeoJsonProperties
            >;
          } else {
            return null;
          }
        }
      ),
      districtsByGeoData: computed(() => {
        const districts: Record<string, Feature> = {};
        const all = items();
        for (const id in all) {
          const geoData = all[id];
          if (!geoData.geoJson) continue;
          const geoJson = JSON.parse(geoData.geoJson);
          districts[id] = geoJson.features;
        }
        return districts;
      }),
      allFeatures: computed((): FeatureProperties[] => {
        const districts: Record<string, FeatureProperties> = {};
        const all = items();
        for (const id in all) {
          const geoData = all[id];
          if (!geoData.geoJson) continue;
          const geoJson = JSON.parse(geoData.geoJson);
          geoJson.features.forEach((feature: Feature) => {
            districts[feature.properties.key] = {
              ...feature.properties,
              ...{ type: feature.type, geoData: id },
            };
          });
        }
        return Object.values(districts);
      }),
    })),
    withMethods(({ featuresFromSelection, items, filter, allFeatures }) => ({
      getFeaturesSorted(
        property: 'key' | 'name',
        order: 'asc' | 'desc' = 'asc'
      ) {
        return featuresFromSelection().sort(function (
          a: GeoJSON.Feature<
            GeoJSON.Point | GeoJSON.Polygon,
            GeoJSON.GeoJsonProperties
          >,
          b: GeoJSON.Feature<
            GeoJSON.Point | GeoJSON.Polygon,
            GeoJSON.GeoJsonProperties
          >
        ) {
          let valA, valB;

          if (!a.properties || !b.properties) return;
          // check for number
          if (property === 'key') {
            valA = isNaN(a.properties[property])
              ? a.properties[property]
              : parseInt(a.properties[property]);
            valB = isNaN(b.properties[property])
              ? b.properties[property]
              : parseInt(b.properties[property]);
          } else {
            valA = a.properties[property];
            valB = b.properties[property];
          }

          if (order === 'asc') {
            return valA > valB ? 1 : valB > valA ? -1 : 0;
          } else {
            return valB > valA ? 1 : valA > valB ? -1 : 0;
          }
        });
      },
      geoJsonById(
        id: string,
        groupFilterKey?: string
      ): FeatureCollection | null {
        const geoData = items()[id];
        if (!geoData || !geoData.geoJson) return null;
        const geoJson = JSON.parse(
          geoData.geoJson
        ) as GeoJSON.FeatureCollection<
          GeoJSON.Point | GeoJSON.Polygon,
          GeoJSON.GeoJsonProperties
        >;

        return groupFilterKey
          ? filterGeoJsonFeaturesByGroup(geoJson, groupFilterKey)
          : geoJson;
      },
      globalSearch(query: string) {
        const itemsArray = allFeatures();
        const tempFilter: StateFilter = { ...filter(), query };
        return filterItems(itemsArray, tempFilter);
      },
    }))
  );
}
